/**
 * Lookup reference-data API wrappers for the DM SPA.
 *
 * Every call accepts an optional `caseId`:
 *   - undefined / null  -> account-global scope ("CaseId" IS NULL)
 *   - a case UUID       -> that case's rows only
 *
 * caseId is always sent as a query param (the backend reads it from the query
 * string first), so the same value reaches GET/POST/PUT/DELETE and uploads.
 */
import { dmClient } from '../../services/dmClient';

function scopeParams(caseId, extra = {}) {
  const params = { ...extra };
  if (caseId) params.caseId = caseId;
  return params;
}

export const lookupApi = {
  list: async ({ caseId, category, search, page = 1, pageSize = 100 } = {}) => {
    const params = scopeParams(caseId, { page, pageSize });
    if (category) params.category = category;
    if (search) params.q = search;
    const resp = await dmClient.get('/lookup', { params });
    return resp.data;
  },

  categories: async ({ caseId } = {}) => {
    const resp = await dmClient.get('/lookup/categories', { params: scopeParams(caseId) });
    return resp.data;
  },

  byCode: async ({ caseId, code, category } = {}) => {
    const params = scopeParams(caseId, { code });
    if (category) params.category = category;
    const resp = await dmClient.get('/lookup/by-code', { params });
    return resp.data;
  },

  get: async (lookupId, { caseId } = {}) => {
    const resp = await dmClient.get(`/lookup/${lookupId}`, { params: scopeParams(caseId) });
    return resp.data;
  },

  create: async (payload, { caseId } = {}) => {
    const resp = await dmClient.post('/lookup', payload, { params: scopeParams(caseId) });
    return resp.data;
  },

  update: async (lookupId, payload, { caseId } = {}) => {
    const resp = await dmClient.put(`/lookup/${lookupId}`, payload, { params: scopeParams(caseId) });
    return resp.data;
  },

  remove: async (lookupId, { caseId } = {}) => {
    const resp = await dmClient.delete(`/lookup/${lookupId}`, { params: scopeParams(caseId) });
    return resp.data;
  },

  previewUpload: async (file, options, { caseId } = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('options', JSON.stringify(options || {}));
    const resp = await dmClient.post('/lookup/upload/preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      params: scopeParams(caseId),
    });
    return resp.data;
  },

  commitUpload: async (file, options, { caseId } = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('options', JSON.stringify(options || {}));
    const resp = await dmClient.post('/lookup/upload/commit', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      params: scopeParams(caseId),
    });
    return resp.data;
  },

  // CMS pack is account-global only; no caseId is sent.
  ingestCmsPack: async (files, options) => {
    const formData = new FormData();
    (files || []).forEach((f) => formData.append('files', f));
    formData.append('options', JSON.stringify(options || {}));
    const resp = await dmClient.post('/lookup/upload/cms-pack', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 900000,
    });
    return resp.data;
  },
};

export default lookupApi;
